import React, { useState, useEffect } from "react";
import adminApi from "../../services/adminApi";
import "./Admin.scss";

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [secret, setSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("analytics");
  const [data, setData] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [userSessions, setUserSessions] = useState([]);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [hideZeroMessages, setHideZeroMessages] = useState(false);

  useEffect(() => {
    // Check if secret is in URL params or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const urlSecret = urlParams.get("secret");
    const storedSecret = localStorage.getItem("lovish-ka-raaz");
    const envSecret = process.env.REACT_APP_ADMIN_SECRET_KEY;

    // Only authenticate if we have a valid secret from URL or localStorage
    if (urlSecret && envSecret && urlSecret === envSecret) {
      setSecret(urlSecret);
      localStorage.setItem("lovish-ka-raaz", urlSecret);
      authenticate(urlSecret);
    } else if (storedSecret && envSecret && storedSecret === envSecret) {
      setSecret(storedSecret);
      authenticate(storedSecret);
    }
    // Removed auto-authentication - user must explicitly provide the secret
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authenticate = (secretValue) => {
    setLoading(true);
    setError("");
    const envSecret = process.env.REACT_APP_ADMIN_SECRET_KEY;
          if (envSecret && secretValue === envSecret) {
        adminApi.setSecret(secretValue);
        setIsAuthenticated(true);
        loadAnalytics();
      } else {
      setError("Invalid secret key");
      localStorage.removeItem("lovish-ka-raaz");
      setIsAuthenticated(false);
    }
    setLoading(false);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    const envSecret = process.env.REACT_APP_ADMIN_SECRET_KEY;

    // Ensure environment secret exists and matches
    if (!envSecret) {
      setError("Admin access not configured");
      return;
    }

    if (secret.trim() === envSecret) {
      authenticate(secret.trim());
    } else {
      setError("Invalid secret key");
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const analytics = await adminApi.getAnalytics();
      setData((prev) => ({ ...prev, analytics }));
    } catch (err) {
      setError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async (tab) => {
    if (data[tab]) return; // Already loaded

    setLoading(true);
    try {
      let result;
      console.log("tab", tab);
      switch (tab) {
        case "chats":
          result = await adminApi.getChats(5000);
          break;
        case "users":
          result = await adminApi.getUsers(2000);

          break;
        case "tools":
          result = await adminApi.getTools(1000);
          break;
        case "analytics":
          result = await adminApi.getAnalytics();
          break;
        default:
          return;
      }
      setData((prev) => ({ ...prev, [tab]: result }));
    } catch (err) {
      setError(`Failed to load ${tab} data`);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    loadTabData(tab);
  };

  const logout = () => {
    localStorage.removeItem("lovish-ka-raaz");
    setIsAuthenticated(false);
    setSecret("");
    setData({});
    setSelectedUser(null);
    setSelectedSession(null);
    setUserSessions([]);
    setSessionMessages([]);
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setSelectedSession(null);
    setSessionMessages([]);
    setLoading(true);

    try {
      // The user data now contains all sessions, so we can use it directly
      if (user.sessions && user.sessions.length > 0) {
        const sessions = user.sessions.map((session) => ({
          sessionId: session.sessionId,
          messages: session.chats || [],
          startTime: session.firstChat,
          lastActivity: session.lastChat,
          totalChats: session.totalChats,
          totalToolCalls: session.totalToolCalls,
          toolCalls: session.toolCalls || [],
        }));

        setUserSessions(sessions);
      } else {
        setUserSessions([]);
      }
    } catch (err) {
      console.error("Error processing user sessions:", err);
      setError("Failed to load user sessions");
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    setLoading(true);

    try {
      // Get the detailed session chat using the proper endpoint
      const sessionData = await adminApi.getSession(session.sessionId);
      const messages = sessionData.messages || session.messages;
      setSessionMessages(messages);
    } catch (err) {
      console.error("Error loading session chat:", err);
      // Fallback to the messages we already have
      setSessionMessages(session.messages);
    } finally {
      setLoading(false);
    }
  };

  const goBackToUsers = () => {
    setSelectedUser(null);
    setSelectedSession(null);
    setUserSessions([]);
    setSessionMessages([]);
  };

  const goBackToSessions = () => {
    setSelectedSession(null);
    setSessionMessages([]);
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h1>Admin Access</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter admin secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              className="secret-input"
            />
            <button type="submit" disabled={loading}>
              {loading ? "Authenticating..." : "Access Admin"}
            </button>
          </form>
          {error && <div className="error">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button onClick={logout} className="logout-btn">
          Logout
        </button>
      </div>

              <div className="admin-tabs">
          {["analytics", "chats", "users", "tools"].map((tab) => (
            <button
              key={tab}
              className={`tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => handleTabChange(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

      {loading && <div className="loading">Loading...</div>}
      {error && <div className="error">{error}</div>}

              <div className="admin-content">
          {activeTab === "chats" && <ChatsView data={data.chats} />}
          {activeTab === "users" &&
            (selectedUser ? (
              selectedSession ? (
                <SessionChatView
                  session={selectedSession}
                  messages={sessionMessages}
                  onBack={goBackToSessions}
                />
              ) : (
                <UserSessionsView
                  user={selectedUser}
                  sessions={userSessions}
                  onSessionClick={handleSessionClick}
                  onBack={goBackToUsers}
                />
              )
            ) : (
              <UsersView
                data={data.users}
                onUserClick={handleUserClick}
                hideZeroMessages={hideZeroMessages}
                onToggleFilter={setHideZeroMessages}
              />
            ))}
          {activeTab === "tools" && <ToolsView data={data.tools} />}
          {activeTab === "analytics" && (
            <AnalyticsView data={data?.analytics?.analytics} />
          )}
        </div>
    </div>
  );
};



// Chats View Component
const ChatsView = ({ data }) => {
  if (!data) return <div>Loading chats...</div>;

  console.log("Chats data:", data);

  return (
    <div className="chats-view">
      <h3>Recent Chats ({data.total})</h3>
      <div className="chats-list">
        {data.chats?.map((chat) => (
          <div key={chat.id} className="chat-item">
            <div className="chat-header">
              <span className="session-id">Session: {chat.sessionId}</span>
              <span className="timestamp">
                {new Date(chat.timestamp).toLocaleString()}
              </span>
            </div>
            <div className="chat-messages">
              <div className="user-message">
                <strong>User:</strong> {chat.userMessage}
              </div>
              <div className="assistant-message">
                <strong>Assistant:</strong>{" "}
                {chat.assistantMessage?.substring(0, 200)}...
              </div>
            </div>
            {chat.toolOutputs?.length > 0 && (
              <div className="tool-outputs">
                <strong>Tools used:</strong>{" "}
                {chat.toolOutputs.map((tool) => tool.type).join(", ")}
              </div>
            )}
            <div
              className="chat-debug"
              style={{
                fontSize: "10px",
                color: "#999",
                marginTop: "5px",
                padding: "5px",
                background: "#f0f0f0",
              }}
            >
              Chat ID: {chat.id} | Raw: {JSON.stringify(chat).substring(0, 150)}
              ...
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Users View Component
const UsersView = ({ data, onUserClick, hideZeroMessages, onToggleFilter }) => {
  if (!data) return <div>Loading users...</div>;

  console.log("Users data:", data);

  // Filter users based on the hideZeroMessages setting
  const filteredUsers = hideZeroMessages
    ? data.users?.filter((user) => user.totalMessages > 0)
    : data.users;

  const zeroMessageUsers =
    data.users?.filter((user) => user.totalMessages === 0) || [];
  const activeUsers =
    data.users?.filter((user) => user.totalMessages > 0) || [];

  return (
    <div className="users-view">
      <div className="users-header">
        <h3>
          Recent Users ({filteredUsers?.length || 0} of {data.total})
        </h3>
        <div className="users-filter">
          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={hideZeroMessages}
              onChange={(e) => onToggleFilter(e.target.checked)}
            />
            <span className="filter-label">Hide users with 0 messages</span>
          </label>
          <div className="filter-stats">
            <span className="active-users">Active: {activeUsers.length}</span>
            <span className="inactive-users">
              Inactive: {zeroMessageUsers.length}
            </span>
          </div>
        </div>
      </div>

      <div className="users-list">
        {filteredUsers?.map((user) => (
          <div
            key={user.id}
            className="user-item"
            onClick={() => onUserClick(user)}
          >
            <div className="user-header">
              <span className="user-name">{user.name || "Anonymous"}</span>
              <span className="user-email">{user.email}</span>
              {user.tags && user.tags.length > 0 && (
                <div className="user-tags">
                  {user.tags.map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="user-stats">
              <span>Messages: {user.totalMessages}</span>
              <span>Sessions: {user.statistics?.totalSessions || 0}</span>
              <span>Status: {user.status}</span>
              <span>
                Last Activity: {new Date(user.lastActivity).toLocaleString()}
              </span>
            </div>
            {user.userInfo?.geolocation && (
              <div className="user-location">
                📍 {user.userInfo.geolocation.city},{" "}
                {user.userInfo.geolocation.country}
              </div>
            )}
            {user.mostRecentChat && (
              <div className="recent-chat-preview">
                <strong>Recent:</strong>{" "}
                {user.mostRecentChat.userMessage?.substring(0, 80)}...
              </div>
            )}
            {user.notes && (
              <div className="user-notes">
                <strong>Notes:</strong> {user.notes}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Tools View Component
const ToolsView = ({ data }) => {
  if (!data) return <div>Loading tools...</div>;

  return (
    <div className="tools-view">
      <h3>Recent Tool Calls ({data.total})</h3>
      <div className="tools-list">
        {data.tools?.map((tool) => (
          <div key={tool.id} className="tool-item">
            <div className="tool-header">
              <span className="tool-type">{tool.type}</span>
              <span className="timestamp">
                {new Date(tool.timestamp).toLocaleString()}
              </span>
            </div>
            <div className="tool-data">
              <pre>{JSON.stringify(tool.data, null, 2)}</pre>
            </div>
            {tool.userInfo && (
              <div className="tool-user-info">
                IP: {tool.userInfo.ip} | Browser: {tool.userInfo.browser?.name}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Analytics View Component
const AnalyticsView = ({ data }) => {
  if (!data) return <div>Loading analytics...</div>;

  console.log("Analytics data:", data);

  return (
    <div className="analytics-view">
      <h3>Analytics Overview</h3>

      <div className="analytics-grid">
        <div className="analytics-card">
          <h4>💬 Chat Statistics</h4>
          <div>Total: {data.chats?.total || 0}</div>
          <div>Today: {data.chats?.today || 0}</div>
          <div>This Week: {data.chats?.thisWeek || 0}</div>
        </div>

        <div className="analytics-card">
          <h4>👥 User Statistics</h4>
          <div>Total Users: {data.users?.total || 0}</div>
          <div>Active Users: {data.users?.active || 0}</div>
        </div>

        <div className="analytics-card">
          <h4>🛠️ Tool Statistics</h4>
          <div>Total Tool Calls: {data.tools?.total || 0}</div>
          <div>Today: {data.tools?.today || 0}</div>
          <div>This Week: {data.tools?.thisWeek || 0}</div>
          <div>
            User Details Recorded: {data.tools?.userDetailsRecorded || 0}
          </div>
          <div>Unknown Questions: {data.tools?.unknownQuestions || 0}</div>
        </div>

        <div className="analytics-card">
          <h4>🔥 Top Sessions</h4>
          {data.activeSessions?.slice(0, 5).map((session, index) => (
            <div key={session._id} className="session-stat">
              <span className="rank">#{index + 1}</span>
              <span className="session-id">
                {session._id.substring(0, 8)}...
              </span>
              <span className="message-count">{session.count} msgs</span>
            </div>
          ))}
        </div>
      </div>

      {data.tools?.commonQuestions && data.tools.commonQuestions.length > 0 && (
        <div className="common-questions-section">
          <h4>❓ Common Questions</h4>
          <div className="questions-list">
            {data.tools.commonQuestions.map((question, index) => (
              <div key={index} className="question-item">
                <span className="question-text">{question._id}</span>
                <span className="question-count">{question.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.users?.topUsers && data.users.topUsers.length > 0 && (
        <div className="top-users-section">
          <h4>⭐ Top Users</h4>
          <div className="top-users-grid">
            {data.users.topUsers.slice(0, 6).map((user, index) => (
              <div key={user._id} className="top-user-card">
                <div className="user-rank">#{index + 1}</div>
                <div className="user-name">{user.name || "Anonymous"}</div>
                <div className="user-stats">
                  <span>{user.totalMessages} messages</span>
                  <span>{user.sessions?.length || 0} sessions</span>
                </div>
                {user.userInfo?.geolocation?.city && (
                  <div className="user-location">
                    📍 {user.userInfo.geolocation.city},{" "}
                    {user.userInfo.geolocation.country}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// User Sessions View Component
const UserSessionsView = ({ user, sessions, onSessionClick, onBack }) => {
  return (
    <div className="user-sessions-view">
      <div className="view-header">
        <button onClick={onBack} className="back-btn">
          ← Back to Users
        </button>
        <h3>User Sessions ({sessions.length})</h3>
      </div>

      <div className="user-info-card">
        <div className="user-details">
          <h4>User Details</h4>
          <p>
            <strong>Name:</strong> {user.name || "Anonymous"}
          </p>
          <p>
            <strong>Email:</strong> {user.email || "Not provided"}
          </p>
          <p>
            <strong>Total Messages:</strong> {user.totalMessages}
          </p>
          <p>
            <strong>Total Sessions:</strong>{" "}
            {user.statistics?.totalSessions || 0}
          </p>
          <p>
            <strong>Total Tool Calls:</strong>{" "}
            {user.statistics?.totalToolCalls || 0}
          </p>
          <p>
            <strong>Status:</strong> {user.status}
          </p>
          {user.userInfo?.geolocation && (
            <p>
              <strong>Location:</strong> 📍 {user.userInfo.geolocation.city},{" "}
              {user.userInfo.geolocation.country}
            </p>
          )}
          {user.notes && (
            <p>
              <strong>Notes:</strong> {user.notes}
            </p>
          )}
        </div>
      </div>

      <div className="sessions-list">
        <h4>Chat Sessions ({sessions.length})</h4>
        {sessions.map((session) => (
          <div
            key={session.sessionId}
            className="session-item"
            onClick={() => onSessionClick(session)}
          >
            <div className="session-header">
              <span className="session-id">Session: {session.sessionId}</span>
              <div className="session-stats">
                <span className="chat-count">
                  {session.totalChats || session.messages.length} chats
                </span>
                <span className="tool-count">
                  {session.totalToolCalls || 0} tools
                </span>
              </div>
            </div>
            <div className="session-details">
              <span>
                Started: {new Date(session.startTime).toLocaleString()}
              </span>
              <span>
                Last Activity: {new Date(session.lastActivity).toLocaleString()}
              </span>
            </div>
            {session.messages.length > 0 && (
              <div className="session-preview">
                <strong>First message:</strong>{" "}
                {session.messages[0].userMessage?.substring(0, 100) ||
                  session.messages[0].content?.substring(0, 100) ||
                  "No message content"}
                ...
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Session Chat View Component
const SessionChatView = ({ session, messages, onBack }) => {
  return (
    <div className="session-chat-view">
      <div className="view-header">
        <button onClick={onBack} className="back-btn">
          ← Back to Sessions
        </button>
        <h3>Chat Session: {session.sessionId}</h3>
      </div>

      <div className="session-info-card">
        <div className="session-details">
          <h4>Session Details</h4>
          <p>
            <strong>Session ID:</strong> {session.sessionId}
          </p>
          <p>
            <strong>Messages:</strong> {messages.length}
          </p>
          <p>
            <strong>Started:</strong>{" "}
            {new Date(session.startTime).toLocaleString()}
          </p>
          <p>
            <strong>Last Activity:</strong>{" "}
            {new Date(session.lastActivity).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="chat-interface">
        <div className="chat-messages">
          {messages.map((message, index) => {
            // Determine message type based on available fields
            let messageType = "user";
            let messageContent = "";
            let messageTime = message.timestamp;

            // Check if it's a new structure with type field
            if (message.type) {
              messageType = message.type;
              messageContent = message.content || "";
            } else {
              // Handle old structure - determine type by which field exists
              if (message.userMessage) {
                messageType = "user";
                messageContent = message.userMessage;
              } else if (message.assistantMessage) {
                messageType = "assistant";
                messageContent = message.assistantMessage;
              } else if (message.content) {
                // If only content exists, assume it's user message
                messageType = "user";
                messageContent = message.content;
              }
            }

            return (
              <div
                key={message.id || index}
                className={`chat-message ${messageType}`}
              >
                 {messageType === "user" && (
                  <div className="message-user">
                    <div className="message-avatar">👤</div>
                    <div className="message-content">
                      <div className="message-text">{messageContent}</div>
                      <div className="message-time">
                        {new Date(messageTime).toLocaleString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        })}
                      </div>
                    </div>
                  </div>
                )}
                { message.assistantMessage && (
                  <div className="message-assistant">
                    <div className="message-avatar">🤖</div>
                    <div className="message-content">
                      <div className="message-text">
                        {message.assistantMessage}
                      </div>
                      <div className="message-time">
                        {new Date(messageTime).toLocaleString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Show tool outputs if they exist */}
                {message.toolOutputs && message.toolOutputs.length > 0 && (
                  <div className="message-tools">
                    <div className="tools-header">
                      <span>🛠️ Tools Used:</span>
                      {message.toolOutputs.map((tool) => tool.type).join(", ")}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Admin;
